import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import axiosInstance from "../../../axiosInstanceSetup";
import TagInput from "./TagInput";

const OnboardProductFields = ({
  form,
  errors,
  onFieldChange,
  inputSx = {},
}) => {
  const [hierarchy, setHierarchy] = useState([]);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchHierarchy = async () => {
      setLoadingHierarchy(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/parentdomain_domain_subdomain_list/`
        );
        if (isMounted) {
          setHierarchy(response.data?.parent_domains || []);
        }
      } catch (error) {
        console.error("Failed to load domain hierarchy", error);
      } finally {
        if (isMounted) {
          setLoadingHierarchy(false);
        }
      }
    };

    fetchHierarchy();
    return () => {
      isMounted = false;
    };
  }, []);

  const parentOptions = useMemo(
    () =>
      hierarchy.map((parent) => ({
        id: parent.parent_domain_id,
        label: parent.parent_domain,
        domains: parent.domains || [],
      })),
    [hierarchy]
  );

  const domainOptions = useMemo(() => {
    const allDomains = parentOptions.flatMap((parent) =>
      parent.domains.map((domain) => ({
        id: domain.domain_id,
        label: domain.domain,
        parentId: parent.id,
        parentName: parent.label,
        subDomains: domain.sub_domains || [],
      }))
    );
    if (form.parentDomainId) {
      return allDomains.filter(
        (domain) => domain.parentId === form.parentDomainId
      );
    }
    return allDomains;
  }, [parentOptions, form.parentDomainId]);

  const subDomainOptions = useMemo(() => {
    const allSubDomains = domainOptions.flatMap((domain) =>
      domain.subDomains.map((sub) => ({
        id: sub.sub_domain_id,
        label: sub.sub_domain,
        domainId: domain.id,
        domainName: domain.label,
        parentId: domain.parentId,
        parentName: domain.parentName,
      }))
    );

    if (form.domainId) {
      return allSubDomains.filter((sub) => sub.domainId === form.domainId);
    }
    if (form.parentDomainId) {
      return allSubDomains.filter(
        (sub) => sub.parentId === form.parentDomainId
      );
    }
    return allSubDomains;
  }, [domainOptions, form.domainId, form.parentDomainId]);

  useEffect(() => {
    if (form.parentDomainId && domainOptions.length === 1 && !form.domainId) {
      const [domain] = domainOptions;
      onFieldChange("domainId", domain.id);
      onFieldChange("domain", domain.label);
      onFieldChange("parentDomainId", domain.parentId);
      onFieldChange("parentDomain", domain.parentName);
    }
  }, [domainOptions, form.domainId, form.parentDomainId, onFieldChange]);

  useEffect(() => {
    if (form.domainId && subDomainOptions.length === 1 && !form.subDomainId) {
      const [sub] = subDomainOptions;
      onFieldChange("subDomainId", sub.id);
      onFieldChange("subDomain", sub.label);
      onFieldChange("domainId", sub.domainId);
      onFieldChange("domain", sub.domainName);
      onFieldChange("parentDomainId", sub.parentId);
      onFieldChange("parentDomain", sub.parentName);
    }
  }, [subDomainOptions, form.domainId, form.subDomainId, onFieldChange]);

  const handleParentChange = (event) => {
    const parentId = event.target.value;
    if (!parentId) {
      onFieldChange("parentDomainId", "");
      onFieldChange("parentDomain", "");
      onFieldChange("domainId", "");
      onFieldChange("domain", "");
      onFieldChange("subDomainId", "");
      onFieldChange("subDomain", "");
      return;
    }
    const parent = parentOptions.find((option) => option.id === parentId);
    onFieldChange("parentDomainId", parentId);
    onFieldChange("parentDomain", parent?.label || "");
    onFieldChange("domainId", "");
    onFieldChange("domain", "");
    onFieldChange("subDomainId", "");
    onFieldChange("subDomain", "");
  };

  const handleDomainChange = (event) => {
    const domainId = event.target.value;
    if (!domainId) {
      onFieldChange("domainId", "");
      onFieldChange("domain", "");
      onFieldChange("subDomainId", "");
      onFieldChange("subDomain", "");
      return;
    }
    const domain = domainOptions.find((option) => option.id === domainId);
    onFieldChange("domainId", domainId);
    onFieldChange("domain", domain?.label || "");
    if (domain) {
      onFieldChange("parentDomainId", domain.parentId);
      onFieldChange("parentDomain", domain.parentName);
    }
    onFieldChange("subDomainId", "");
    onFieldChange("subDomain", "");
  };

  const handleSubDomainChange = (event) => {
    const subDomainId = event.target.value;
    if (!subDomainId) {
      onFieldChange("subDomainId", "");
      onFieldChange("subDomain", "");
      return;
    }
    const subDomain = subDomainOptions.find(
      (option) => option.id === subDomainId
    );
    onFieldChange("subDomainId", subDomainId);
    onFieldChange("subDomain", subDomain?.label || "");
    if (subDomain) {
      onFieldChange("domainId", subDomain.domainId);
      onFieldChange("domain", subDomain.domainName);
      onFieldChange("parentDomainId", subDomain.parentId);
      onFieldChange("parentDomain", subDomain.parentName);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 2,
        p: 2,
        backgroundColor: "rgba(0,0,160,0.02)",
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} color="#bb1ccc" mb={1.5}>
        Onboard Product Details
      </Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            label="Product Code (optional)"
            value={form.productCode}
            onChange={(e) => onFieldChange("productCode", e.target.value)}
            error={Boolean(errors.productCode)}
            helperText={errors.productCode}
            sx={inputSx}
          />
          <TextField
            fullWidth
            label="Product Name *"
            value={form.productName}
            onChange={(e) => onFieldChange("productName", e.target.value)}
            error={Boolean(errors.productName)}
            helperText={errors.productName}
            sx={inputSx}
          />
        </Stack>
        <TextField
          label="Product Owner Email *"
          type="email"
          value={form.productOwner}
          placeholder="owner@effem.com"
          onChange={(e) => onFieldChange("productOwner", e.target.value)}
          error={Boolean(errors.productOwner)}
          helperText={errors.productOwner || "Must be an @effem.com address"}
          sx={inputSx}
        />
        <TextField
          label="Product Description *"
          multiline
          minRows={3}
          value={form.productDescription}
          onChange={(e) => onFieldChange("productDescription", e.target.value)}
          error={Boolean(errors.productDescription)}
          helperText={errors.productDescription}
          sx={inputSx}
        />
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl
            fullWidth
            sx={inputSx}
            error={Boolean(errors.parentDomain)}
            disabled={loadingHierarchy}
          >
            <InputLabel id="parent-domain-label">Parent Domain *</InputLabel>
            <Select
              labelId="parent-domain-label"
              label="Parent Domain *"
              value={form.parentDomainId || ""}
              onChange={handleParentChange}
            >
              <MenuItem value="">
                <em>
                  {loadingHierarchy
                    ? "Loading parent domains..."
                    : "Select parent domain"}
                </em>
              </MenuItem>
              {parentOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.parentDomain || "Select the parent domain"}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            sx={inputSx}
            error={Boolean(errors.domain)}
            disabled={loadingHierarchy || !domainOptions.length}
          >
            <InputLabel id="domain-label">Domain *</InputLabel>
            <Select
              labelId="domain-label"
              label="Domain *"
              value={form.domainId || ""}
              onChange={handleDomainChange}
            >
              <MenuItem value="">
                <em>
                  {loadingHierarchy
                    ? "Loading domains..."
                    : form.parentDomainId && !domainOptions.length
                      ? "No domains available"
                      : "Select domain"}
                </em>
              </MenuItem>
              {domainOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.domain ||
                (form.parentDomainId
                  ? "Select a domain for the chosen parent"
                  : "Select a domain to auto-fill the parent")}
            </FormHelperText>
          </FormControl>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl
            // fullWidth
            style={{ width: "50%" }}
            sx={inputSx}
            error={Boolean(errors.subDomain)}
            disabled={loadingHierarchy || !subDomainOptions.length}
          >
            <InputLabel id="subdomain-label">Sub Domain *</InputLabel>
            <Select
              labelId="subdomain-label"
              label="Sub Domain *"
              value={form.subDomainId || ""}
              onChange={handleSubDomainChange}
            >
              <MenuItem value="">
                <em>
                  {loadingHierarchy
                    ? "Loading sub domains..."
                    : form.domainId && !subDomainOptions.length
                      ? "No sub domains available"
                      : "Select sub domain"}
                </em>
              </MenuItem>
              {subDomainOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.subDomain ||
                (form.domainId
                  ? "Select a sub domain for the chosen domain"
                  : "Choose a domain to narrow down sub domains")}
            </FormHelperText>
          </FormControl>
        </Stack>
        <TagInput
          label="UC Schema (optional)"
          value={form.ucSchema}
          onChange={(val) => onFieldChange("ucSchema", val)}
          error={Boolean(errors.ucSchema)}
          helperText="Provide Azure UC Schema (mention catalog.schema)"
        />
        <TagInput
          label="UC Schema GCP (optional)"
          value={form.uc_schema_gcp}
          onChange={(val) => onFieldChange("uc_schema_gcp", val)}
          error={Boolean(errors.uc_schema_gcp)}
          helperText="Provide GCP UC Schema (mention catalog.schema)"
        />

        <TagInput
          label="ADLS Path (optional)"
          value={form.adlsPath}
          onChange={(val) => onFieldChange("adlsPath", val)}
          error={Boolean(errors.adlsPath)}
          helperText="Provide ADLS Path"
        />
      </Stack>
    </Box>
  );
};

export default OnboardProductFields;
